import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { setAuthLogoutCallback, clearAuthLogoutCallback } from '../services/http';
import { getBrandKeywordFromUrl } from '../utils/configHelpers';
import { useUserStore } from './userStore';

type AuthState = {
  isAuthenticated: boolean;
  email: string | null;
  phone: string | null;
  token: string | null;
  marca: string | null;
  issuedAt: number | null;
  expiresAt: number | null;
  isLoading: boolean;
};

type AuthAction =
  | { type: 'LOGIN'; payload: { email?: string; phone?: string; token: string; marca?: string; ttlMs: number } }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean };

// Clave legacy para migración automática
const LEGACY_STORAGE_KEY = 'PAYCOSPA_AUTH_V1';

// Función para obtener clave de storage específica por marca
const getStorageKey = (brandKeyword: string): string => {
  return `PAYCOSPA_AUTH_V1_${brandKeyword}`;
};

const initialState: AuthState = {
  isAuthenticated: false,
  email: null,
  phone: null,
  token: null,
  marca: null,
  issuedAt: null,
  expiresAt: null,
  isLoading: true,
};

function crc32(str: string): string {
  // Implementación simple de CRC32 para detectar edición manual en sessionStorage
  let crc = 0 ^ -1;
  for (let i = 0; i < str.length; i++) {
    let c = str.charCodeAt(i);
    crc = (crc >>> 8) ^ table[(crc ^ c) & 0xff];
  }
  return ((crc ^ -1) >>> 0).toString(16);
}

const table = (() => {
  let t = new Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    t[n] = c;
  }
  return t;
})();

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN': {
      const now = Date.now();
      return {
        isAuthenticated: true,
        email: action.payload.email || null,
        phone: action.payload.phone || null,
        token: action.payload.token,
        marca: action.payload.marca || null,
        issuedAt: now,
        expiresAt: now + action.payload.ttlMs,
        isLoading: false,
      };
    }
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
}

type AuthContextValue = {
  state: AuthState;
  login: (params: { email?: string; phone?: string; token: string; marca?: string }, ttlMinutes?: number) => void;
  logout: () => void;
  getToken: () => string | null;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  
  // Extraer brandKeyword de la URL ACTUAL (reactivo a cambios de ruta)
  const brandKeyword = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    return pathParts[0] || 'acme';
  }, [location.pathname]);
  
  const storageKey = useMemo(() => getStorageKey(brandKeyword), [brandKeyword]);

  // --- Utilidades de cifrado ---
  const enc = new TextEncoder();
  const dec = new TextDecoder();

  const toB64 = (buf: ArrayBuffer) => {
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  };
  const fromB64 = (b64: string) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
  };

  const getPassphrase = () => {
    const base = (window as any).PAGOS_CONFIG?.apiBaseUrl || window.location.origin;
    return `lukapay@auth:v1|${base}`;
  };

  const deriveKey = async (passphrase: string) => {
    const salt = enc.encode('payco.auth.salt.v1');
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      enc.encode(passphrase),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  };

  const encryptPayload = async (plain: string) => {
    if (!('crypto' in window) || !crypto.subtle) {
      // fallback: firma + texto plano
      const signature = crc32(plain);
      return JSON.stringify({ m: plain, s: signature, v: 1, e: false });
    }
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveKey(getPassphrase());
    const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plain));
    const cipher = toB64(cipherBuf);
    const ivB64 = toB64(iv.buffer);
    const signature = crc32(plain);
    return JSON.stringify({ c: cipher, i: ivB64, s: signature, v: 1, e: true });
  };

  const decryptPayload = async (blob: string) => {
    try {
      const obj = JSON.parse(blob);
      if (!obj?.v) return null;
      if (obj.e !== true) {
        // fallback no cifrado
        const ok = obj?.m && obj?.s && crc32(obj.m) === obj.s;
        return ok ? obj.m : null;
      }
      const iv = fromB64(obj.i);
      const key = await deriveKey(getPassphrase());
      const cipherBuf = fromB64(obj.c);
      const plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(iv) }, key, cipherBuf);
      const plain = dec.decode(plainBuf);
      const ok = crc32(plain) === obj.s;
      return ok ? plain : null;
    } catch {
      return null;
    }
  };

  const persist = useCallback(async (s: AuthState) => {
    try {
      const payload = JSON.stringify(s);
      const blob = await encryptPayload(payload);
      localStorage.setItem(storageKey, blob);
    } catch {
      // ignore
    }
  }, [storageKey]);

  const restore = useCallback(async () => {
    console.log('[Auth:restore] Starting restore for brandKeyword:', brandKeyword, 'storageKey:', storageKey);
    try {
      // --- MIGRACIÓN AUTOMÁTICA desde clave legacy ---
      const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
      const currentRaw = localStorage.getItem(storageKey);
      
      if (legacyRaw && !currentRaw) {
        const legacyPayload = await decryptPayload(legacyRaw);
        if (legacyPayload) {
          const legacyState: AuthState = JSON.parse(legacyPayload);
          if (!legacyState.marca || legacyState.marca === brandKeyword) {
            console.log('[Auth:restore] Migrating legacy session to brand-specific key:', brandKeyword);
            localStorage.setItem(storageKey, legacyRaw);
            localStorage.removeItem(LEGACY_STORAGE_KEY);
          }
        }
      }
      // --- FIN MIGRACIÓN ---
      
      const raw = localStorage.getItem(storageKey);
      console.log('[Auth:restore] Raw token exists:', !!raw);
      if (!raw) {
        console.log('[Auth:restore] No token found, finishing');
        return;
      }
      
      const payload = await decryptPayload(raw);
      console.log('[Auth:restore] Decrypt successful:', !!payload);
      if (!payload) {
        console.warn('[Auth:restore] Decrypt failed, removing invalid token');
        localStorage.removeItem(storageKey);
        return;
      }
      
      const s: AuthState = JSON.parse(payload);
      console.log('[Auth:restore] Parsed state - marca:', s.marca, 'expiresAt:', s.expiresAt, 'now:', Date.now());
      
      // CRÍTICO: NO borrar el token por mismatch de marca
      // En su lugar, normalizar la marca al brandKeyword actual
      // La sesión ya está en la clave correcta (storageKey), así que es válida para esta marca
      if (s.marca && s.marca !== brandKeyword) {
        console.log('[Auth:restore] Normalizing marca from', s.marca, 'to', brandKeyword);
        s.marca = brandKeyword;
        // Re-persistir con la marca corregida
        const correctedPayload = JSON.stringify(s);
        const correctedBlob = await encryptPayload(correctedPayload);
        localStorage.setItem(storageKey, correctedBlob);
      }
      
      if (!s?.expiresAt || s.expiresAt <= Date.now()) {
        console.warn('[Auth:restore] Token expired, removing');
        localStorage.removeItem(storageKey);
        return;
      }
      
      console.log('[Auth:restore] Token valid, dispatching LOGIN');
      dispatch({ 
        type: 'LOGIN', 
        payload: { 
          email: s.email || undefined, 
          phone: s.phone || undefined, 
          token: s.token || '', 
          marca: brandKeyword, // Siempre usar brandKeyword como marca
          ttlMs: s.expiresAt - Date.now() 
        } 
      });
    } catch (err) {
      console.error('[Auth:restore] Error during restore:', err);
    }
  }, [brandKeyword, storageKey]);

  useEffect(() => {
    (async () => { 
      await restore();
      dispatch({ type: 'SET_LOADING', payload: false });
    })();
  }, [restore]);

  // Persistir SOLO cuando hay sesión activa
  // NO borrar automáticamente - solo se borra en logout() explícito o restore() con token inválido
  useEffect(() => {
    if (state.isAuthenticated) {
      // Si el cambio vino de otra pestaña (storage event), no re-persistir
      if (skipNextPersistRef.current) {
        skipNextPersistRef.current = false;
        return;
      }
      persist(state);
    }
  }, [state, persist]);

  // Expiración automática
  useEffect(() => {
    if (!state.isAuthenticated || !state.expiresAt) return;
    const ms = Math.max(0, state.expiresAt - Date.now());
    const t = setTimeout(() => dispatch({ type: 'LOGOUT' }), ms);
    return () => clearTimeout(t);
  }, [state.isAuthenticated, state.expiresAt]);

  // Sincronización entre pestañas/ventanas - solo reaccionar a cambios de NUESTRA marca
  // CRÍTICO: NO llamar restore() aquí porque generaría un loop infinito:
  // persist → storage event → restore → LOGIN (nuevo issuedAt) → persist → ...
  const skipNextPersistRef = React.useRef(false);
  
  useEffect(() => {
    const handler = async (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      
      // Si se removió la clave, la otra pestaña cerró sesión → hacer lo mismo
      if (!e.newValue) {
        console.log('[Auth:storage] Session removed in another tab, logging out');
        // Limpiar store global de usuario
        useUserStore.getState().clearUser();
        dispatch({ type: 'LOGOUT' });
        // Hard redirect igual que logout explícito
        const currentPath = window.location.pathname;
        const pathParts = currentPath.split('/').filter(Boolean);
        const bk = pathParts.length > 0 ? pathParts[0] : null;
        const homePath = bk && bk !== 'payment' && bk !== 'success' ? `/${bk}` : '/';
        window.location.href = homePath;
        return;
      }
      
      // Si hay nuevo valor, leer directamente del evento (no de localStorage)
      // y solo actualizar si el token realmente cambió
      try {
        const payload = await decryptPayload(e.newValue);
        if (!payload) return;
        const s: AuthState = JSON.parse(payload);
        if (!s?.expiresAt || s.expiresAt <= Date.now()) return;
        
        // Solo actualizar si el token es diferente al actual
        if (s.token === state.token) return;
        
        console.log('[Auth:storage] Token changed in another tab, syncing');
        skipNextPersistRef.current = true; // evitar re-persistir este cambio
        dispatch({
          type: 'LOGIN',
          payload: {
            email: s.email || undefined,
            phone: s.phone || undefined,
            token: s.token || '',
            marca: brandKeyword,
            ttlMs: s.expiresAt - Date.now(),
          },
        });
      } catch {
        // ignore decrypt errors
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storageKey, state.token, brandKeyword]);

  // Registrar callback para logout automático cuando expira la sesión (401 de APIs)
  useEffect(() => {
    const handleSessionExpired = () => {
      dispatch({ type: 'LOGOUT' });
      // Redirigir al home
      const currentPath = window.location.pathname;
      const pathParts = currentPath.split('/').filter(Boolean);
      // Si hay una marca en la URL, redirigir a /{marca}, sino a /
      const brandKeyword = pathParts.length > 0 ? pathParts[0] : null;
      const homePath = brandKeyword && brandKeyword !== 'payment' && brandKeyword !== 'success' 
        ? `/${brandKeyword}` 
        : '/';
      window.location.href = homePath;
    };
    
    setAuthLogoutCallback(handleSessionExpired);
    return () => clearAuthLogoutCallback();
  }, []);

  const login = useCallback((params: { email?: string; phone?: string; token: string; marca?: string }, ttlMinutes: number = 15 * 24 * 60) => {
    // CRÍTICO: Siempre usar brandKeyword como marca, ignorar params.marca
    // Esto asegura que la sesión se guarde con el slug de la URL, no con lo que venga del API
    console.log('[Auth:login] Logging in with brandKeyword:', brandKeyword, 'ignoring params.marca:', params.marca);
    dispatch({ type: 'LOGIN', payload: { ...params, marca: brandKeyword, ttlMs: ttlMinutes * 60 * 1000 } });
    // Guardar última marca para redirect desde /
    localStorage.setItem('last_brand_keyword', brandKeyword);
  }, [brandKeyword]);

  const logout = useCallback(() => {
    // CRÍTICO: Borrar el token de sesión PRIMERO
    localStorage.removeItem(storageKey);
    
    // Limpiar store global de usuario
    useUserStore.getState().clearUser();
    
    // Limpiar datos de empresa del localStorage CON SUFIJO de marca
    const keysToRemove = [
      `company_name_${brandKeyword}`,
      `company_phone_${brandKeyword}`,
      `company_avatar_${brandKeyword}`,
      `company_address_${brandKeyword}`,
      `user_name_${brandKeyword}`,
      // También limpiar claves legacy por compatibilidad
      'company_name',
      'company_phone',
      'company_avatar',
      'company_address',
      'user_name'
    ];
    keysToRemove.forEach(key => {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    });
    
    dispatch({ type: 'LOGOUT' });
  }, [brandKeyword, storageKey]);

  const getToken = useCallback(() => state.token, [state.token]);

  const value = useMemo(() => ({ state, login, logout, getToken }), [state, login, logout, getToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}


